import Badge from '@/components/public/ui/Badge';
import Icon from '@/components/public/ui/Icon';
import type { EventItem } from '@/components/public/cards/EventCard';
import { eventDay, eventMonth, eventTime } from '@/lib/datetime';

interface EventListViewProps {
  events: EventItem[];
}

const badgeFor: Record<string, 'primary' | 'secondary' | 'tertiary'> = {
  Assembleia: 'secondary',
  Cultural: 'primary',
  Desporto: 'tertiary',
  Infantil: 'primary',
};

function formatMonth(isoString: string): string {
  return eventMonth(isoString).toUpperCase();
}

// Display-only chronological list (events have no detail page → no row link/arrow).
export default function EventListView({ events }: EventListViewProps) {
  return (
    <div className="relative flex flex-col gap-3 pl-10 md:pl-12">
      {/* Spine line */}
      <div className="bg-line absolute top-2 bottom-2 left-5 w-px" />

      {events.map((event) => {
        const badge = badgeFor[event.category ?? ''] ?? 'primary';
        const isPast = event.startsAt ? new Date(event.startsAt) < new Date() : false;

        return (
          <div
            key={event.id}
            className="surface-card hover:border-line-strong flex items-center gap-4 p-4 transition-all duration-200 hover:shadow-md"
          >
            {/* Date */}
            <div className="w-14 flex-shrink-0 text-center">
              {event.startsAt && (
                <>
                  <div className="font-headline text-primary text-2xl leading-none font-bold tracking-tight">
                    {eventDay(event.startsAt)}
                  </div>
                  <div className="text-on-surface-light mt-0.5 text-[0.55rem] font-semibold tracking-widest uppercase">
                    {formatMonth(event.startsAt)}
                  </div>
                </>
              )}
              {isPast && (
                <div className="badge-completed mt-1.5 rounded-full border px-1.5 py-0.5 text-[9px] font-bold tracking-wide">
                  Realizado
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="bg-line h-9 w-px flex-shrink-0" />

            {/* Info */}
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                {event.category && <Badge label={event.category} color={badge} />}
                {event.startsAt && (
                  <span className="text-on-surface-light text-xs">{eventTime(event.startsAt)}</span>
                )}
              </div>
              <div className="font-headline text-on-surface text-sm leading-snug font-semibold">
                {event.title}
              </div>
              {event.location && (
                <div className="text-on-surface-light mt-0.5 flex items-center gap-1 text-xs">
                  <Icon name="location_on" size="sm" />
                  <span className="truncate">{event.location}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
